import type { ConversationKanbanCard } from '@troia-v3/types';
import { getIntegrationLabel } from '@/shared/icons';
import type { AttendanceCardConversation } from '../../types';
import type { ConversationCardAgentChip, ConversationCardFacts } from './types';

/**
 * Discriminante da union. `messageCount` é OBRIGATÓRIO em `Conversation` e não
 * existe na projeção do board — ao contrário de `unreadCount`, que é opcional no
 * board e por isso não serve de discriminante em runtime.
 */
const isBoardCard = (c: AttendanceCardConversation): c is ConversationKanbanCard =>
  !('messageCount' in c);

const resolveAgentChip = (
  agentId: string | undefined,
  agentStatus: 'active' | 'inactive' | 'paused' | undefined,
): ConversationCardAgentChip | null => {
  if (!agentId) return null;
  if (agentStatus === 'paused') return { label: 'IA pausada', icon: 'pause' };
  if (agentStatus === 'active') return { label: 'IA ativa', icon: 'zap' };
  return null;
};

export interface BuildConversationCardFactsOptions {
  /** Id do usuário logado — resolve `unreadCount` no shape completo. */
  currentUserId?: string;
  /** Id de integração vindo da página (`card.providerType`). */
  providerId?: string;
}

export const buildConversationCardFacts = (
  conversation: AttendanceCardConversation,
  options: BuildConversationCardFactsOptions = {},
): ConversationCardFacts => {
  const board = isBoardCard(conversation) ? conversation : undefined;
  const full = isBoardCard(conversation) ? undefined : conversation;

  const unreadCount = board
    ? board.unreadCount ?? 0
    : options.currentUserId
      ? full?.unreadTracking?.[options.currentUserId]?.count ?? 0
      : 0;

  // Direção só chega pela projeção do board (Tarefa 14). Sem direção, assumimos
  // que a vez é nossa: num painel de fila, deixar de sinalizar cliente esperando
  // é pior que sinalizar a mais.
  const lastMessageDirection = board?.lastMessageDirection;

  // Snapshot de SLA só existe na projeção do board (Plano D) — o shape
  // completo não o carrega, pelo mesmo motivo de `lastMessageDirection`.
  const sla = board?.sla;

  return {
    id: conversation.id,
    status: conversation.status,
    contactName: conversation.contact?.name || conversation.subject || 'Sem nome',
    contactPhone: conversation.contact?.phone,
    contactPicture: conversation.contact?.picture,
    assigneeName: conversation.assignee?.name,
    assigneePicture: conversation.assignee?.picture,
    channelLabel:
      board?.channelName ??
      full?.provider?.name ??
      (options.providerId ? getIntegrationLabel(options.providerId) : undefined),
    providerId: options.providerId,
    lastMessage: conversation.lastMessage,
    lastMessageAt: conversation.lastMessageAt ?? conversation.updatedAt,
    lastMessageDirection,
    waitingOnUs: lastMessageDirection !== 'outbound',
    ...(sla ? { sla } : {}),
    unreadCount,
    // `startedAt` do board é CRU (Tarefa 12); no shape completo ele já vem
    // coalescido com `createdAt` e a conta de fila daria zero — por isso o par
    // só é útil quando a origem é o board, e o card trata ausência como
    // "ainda na fila". `exactOptionalPropertyTypes` obriga o espalhamento.
    ...(conversation.startedAt ? { startedAt: conversation.startedAt } : {}),
    ...(conversation.createdAt ? { createdAt: conversation.createdAt } : {}),
    agentChip: resolveAgentChip(conversation.agentId, conversation.agentStatus),
  };
};
